/**
 * "جلسة تصوير" — the doctor books a promotional photography crew.
 *
 * Three steps on one screen: pick a crew (with their sample work visible before
 * committing), pick a length, then pick a slot from a calendar of days the crew
 * actually works.
 *
 * Availability comes from the server, never from arithmetic here. Every booking
 * reserves the crew's travel buffer either side of itself, so the free slots on
 * a day are not simply "the working window minus the booked hours" — they are
 * whatever survives the buffered windows. Recomputing that in the browser would
 * drift the moment an admin changed a team's gap.
 */

import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  EmptyState,
  Field,
  Icon,
  Input,
  Modal,
  Panel,
  SectionTitle,
  Spinner,
  Textarea,
} from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { useAsync } from '../../hooks/useAsync';
import { useDebounced } from '../../hooks/useLocalStorage';
import { photographyApi } from '../../api/endpoints';
import { formatCurrency } from '../../domain/pricing';
import { addDays, formatTime12, isoToDisplayDate, todayKey, ARABIC_DAYS } from '../../domain/dates';
import { timeRange } from '../../domain/bidi';

/** How far ahead a crew can be booked. */
const WINDOW_DAYS = 30;

const STATUS_META = {
  pending: { label: 'بانتظار التأكيد', className: 'border-amber-500/30 bg-amber-500/10 text-amber-300' },
  confirmed: { label: 'مؤكد', className: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' },
  completed: { label: 'تم التصوير', className: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-300' },
  cancelled: { label: 'ملغي', className: 'border-red-500/30 bg-red-500/10 text-red-300' },
};

/** JS getDay() is Sun=0…Sat=6; the crew stores lowercase English names. */
const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const dayKeyOf = (dateKey) => DAY_KEYS[new Date(`${dateKey}T00:00:00`).getDay()];

export function PhotographyBooking() {
  const { doctorId } = useAuth();
  const { success, error: toastError } = useNotifications();

  const teamsState = useAsync(() => photographyApi.teams(), [], { initialData: [] });
  const bookingsState = useAsync(() => photographyApi.bookings(), [doctorId], {
    initialData: [],
  });

  const teams = useMemo(() => teamsState.data || [], [teamsState.data]);
  const myBookings = useMemo(() => bookingsState.data || [], [bookingsState.data]);

  const [teamId, setTeamId] = useState(null);
  const [hours, setHours] = useState(2);
  const [dateKey, setDateKey] = useState(todayKey());
  // The location decides which gap applies, so it is chosen before the slot
  // rather than after — otherwise the calendar would show the cautious answer
  // and then silently change once the doctor typed an address.
  const [location, setLocation] = useState('');
  const [confirming, setConfirming] = useState(null);
  const [portfolioTeam, setPortfolioTeam] = useState(null);

  // Refetching on every keystroke would hammer the endpoint mid-word.
  const debouncedLocation = useDebounced(location, 400);

  const team = useMemo(
    () => teams.find((t) => String(t.id) === String(teamId)) || null,
    [teams, teamId],
  );

  // Clamp the requested length into whatever this crew accepts, so switching
  // teams cannot leave an impossible duration selected.
  const boundedHours = team
    ? Math.min(Math.max(hours, team.minHours || 1), team.maxHours || 6)
    : hours;

  const availability = useAsync(
    () =>
      team
        ? photographyApi.availability({
            teamId: team.id,
            date: dateKey,
            hours: boundedHours,
            location: location.trim(),
          })
        : Promise.resolve({ slots: [] }),
    [teamId, dateKey, boundedHours, debouncedLocation],
    { initialData: { slots: [] }, enabled: Boolean(team) },
  );

  /** What the gap works out to today, given what is already on the crew's day. */
  const bookedToday = useMemo(
    () => availability.data?.bookedToday || [],
    [availability.data],
  );
  const gapToday = useMemo(() => {
    if (bookedToday.length === 0) return null;
    const anyElsewhere = bookedToday.some((b) => !b.sameLocation);
    return {
      minutes: anyElsewhere
        ? availability.data?.bufferMinutes
        : availability.data?.sameLocationBufferMinutes,
      sameLocation: !anyElsewhere,
      count: bookedToday.length,
    };
  }, [bookedToday, availability.data]);

  const dates = useMemo(
    () => Array.from({ length: WINDOW_DAYS }, (_, i) => addDays(todayKey(), i)),
    [],
  );

  const slots = availability.data?.slots || [];
  const total = team ? Math.round((team.hourlyRate || 0) * boundedHours) : 0;

  const submit = async (payload) => {
    try {
      await photographyApi.book(payload);
      success('تم إرسال طلب التصوير — بانتظار تأكيد الفريق');
      setConfirming(null);
      bookingsState.reload();
      availability.reload();
    } catch (err) {
      // 409 is the server's "someone took this window while you were deciding",
      // which includes the travel gap either side.
      if (err.status === 409) {
        toastError('هذا الموعد لم يعد متاحاً — اختر موعداً آخر');
        availability.reload();
      } else {
        toastError(err.message || 'تعذر إتمام الحجز');
      }
    }
  };

  const cancel = async (booking) => {
    try {
      await photographyApi.setStatus(booking.id, 'cancelled');
      success('تم إلغاء الحجز');
      bookingsState.reload();
    } catch (err) {
      toastError(err.message || 'تعذر إلغاء الحجز');
    }
  };

  return (
    <div className="space-y-6">
      {/* --- 1. the crew --------------------------------------------------- */}
      <Panel>
        <SectionTitle icon="camera">اختر فريق التصوير</SectionTitle>

        {teamsState.loading && teams.length === 0 ? (
          <Spinner variant="inline" label="جاري تحميل الفرق..." />
        ) : teams.length === 0 ? (
          <EmptyState
            icon="camera"
            title="لا توجد فرق تصوير متاحة"
            message="سيتم إضافة فرق التصوير من الإدارة قريباً."
          />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {teams.map((item) => {
              const active = String(item.id) === String(teamId);
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setTeamId(item.id)}
                  aria-pressed={active}
                  className={`rounded-2xl border p-4 text-right transition-all ${
                    active
                      ? 'border-purple-500 bg-purple-500/10'
                      : 'border-white/10 bg-slate-900 hover:border-white/25'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <span
                      className={`rounded-xl p-2.5 ${
                        active ? 'bg-purple-500/20 text-purple-300' : 'bg-white/5 text-slate-400'
                      }`}
                    >
                      <Icon name="camera" size={20} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-bold text-white">{item.name}</p>
                      <p className="mt-0.5 text-xs text-emerald-400">
                        {formatCurrency(item.hourlyRate)} / ساعة
                      </p>
                      <p className="mt-1 text-[11px] text-slate-500">
                        {timeRange(item.minHours, item.maxHours)} ساعات · انتقال {item.bufferMinutes} دقيقة
                      </p>
                    </div>
                  </div>

                  {item.bio && (
                    <p className="mt-2 line-clamp-2 text-[11px] leading-relaxed text-slate-400">
                      {item.bio}
                    </p>
                  )}

                  {(item.portfolio || []).length > 0 && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(event) => {
                        event.stopPropagation();
                        setPortfolioTeam(item);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.stopPropagation();
                          setPortfolioTeam(item);
                        }
                      }}
                      className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg bg-white/5 py-2 text-[11px] font-bold text-cyan-300 transition-colors hover:bg-white/10"
                    >
                      <Icon name="image" size={13} />
                      شاهد أعمال الفريق ({item.portfolio.length})
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </Panel>

      {/* --- 2. length and day --------------------------------------------- */}
      {team && (
        <Panel>
          <SectionTitle icon="clock">مدة التصوير واليوم</SectionTitle>

          <Field
            label="عدد الساعات"
            hint={`هذا الفريق يقبل من ${team.minHours} إلى ${team.maxHours} ساعات`}
          >
            <div className="flex flex-wrap gap-2">
              {Array.from(
                { length: (team.maxHours || 6) - (team.minHours || 1) + 1 },
                (_, i) => (team.minHours || 1) + i,
              ).map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setHours(value)}
                  aria-pressed={boundedHours === value}
                  className={`rounded-xl border px-4 py-2 text-sm font-bold transition-colors ${
                    boundedHours === value
                      ? 'border-purple-500 bg-purple-500/15 text-purple-200'
                      : 'border-white/10 bg-slate-900 text-slate-300 hover:border-white/25'
                  }`}
                >
                  {value} ساعة
                </button>
              ))}
            </div>
          </Field>

          <div className="mt-4">
            <Field
              label="مكان التصوير"
              hint="يحدد وقت الانتقال المطلوب — تصويران في نفس المكان يحتاجان وقتاً أقل بكثير"
            >
              <Input
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="مثال: العيادة الرئيسية — المعادي"
              />
            </Field>
          </div>

          <div className="custom-scrollbar mt-4 flex gap-2 overflow-x-auto pb-2">
            {dates.map((date) => {
              const works = (team.workingDays || []).includes(dayKeyOf(date));
              const selected = date === dateKey;
              return (
                <button
                  key={date}
                  type="button"
                  disabled={!works}
                  onClick={() => setDateKey(date)}
                  title={works ? undefined : 'الفريق لا يعمل هذا اليوم'}
                  className={`flex w-[74px] shrink-0 flex-col items-center gap-0.5 rounded-xl border p-2.5 transition-colors ${
                    selected
                      ? 'border-purple-500 bg-purple-500/15 text-purple-200'
                      : works
                        ? 'border-white/10 bg-slate-900 text-slate-300 hover:bg-slate-800'
                        : 'cursor-not-allowed border-white/5 bg-slate-950 text-slate-600'
                  }`}
                >
                  <span className="text-[10px]">{ARABIC_DAYS[dayKeyOf(date)]}</span>
                  <span className="text-lg font-bold leading-none">{date.slice(-2)}</span>
                  <span className="text-[9px] opacity-70">{date.slice(5, 7)}</span>
                </button>
              );
            })}
          </div>
        </Panel>
      )}

      {/* --- 3. the slot ---------------------------------------------------- */}
      {team && (
        <Panel>
          <SectionTitle icon="calendar-check">
            المواعيد المتاحة — {isoToDisplayDate(dateKey)}
          </SectionTitle>

          {/* What the gap actually is today, not what the team's default says.
              Two shoots at one address need a short reset; two across town need
              the full travel time, and the calendar changes accordingly. */}
          {gapToday ? (
            <div
              className={`mb-3 rounded-xl border p-3 text-[11px] leading-relaxed ${
                gapToday.sameLocation
                  ? 'border-emerald-500/25 bg-emerald-500/5 text-emerald-200'
                  : 'border-amber-500/25 bg-amber-500/5 text-amber-200'
              }`}
            >
              <p className="flex items-start gap-2">
                <Icon
                  name={gapToday.sameLocation ? 'check-circle-2' : 'info'}
                  size={14}
                  className="mt-px shrink-0"
                />
                <span>
                  في هذا اليوم {gapToday.count} تصوير محجوز.{' '}
                  {gapToday.sameLocation ? (
                    <>
                      وبما أن تصويرك في <strong>نفس المكان</strong>، يكفي الفريق{' '}
                      <strong>{gapToday.minutes} دقيقة</strong> فقط بين التصويرين — لذلك تظهر
                      مواعيد أكثر.
                    </>
                  ) : (
                    <>
                      وبما أن المكان <strong>مختلف</strong>، يحجز الفريق{' '}
                      <strong>{gapToday.minutes} دقيقة</strong> للانتقال قبل التصوير وبعده.
                    </>
                  )}
                </span>
              </p>

              <ul className="mt-2 space-y-0.5 pr-6 text-[10.5px] opacity-80">
                {bookedToday.map((item, index) => (
                  <li key={`${item.startTime}-${index}`}>
                    {formatTime12(item.startTime)} · {item.durationHours} ساعة
                    {item.location ? ` · ${item.location}` : ''}
                    {item.sameLocation ? ' (نفس مكانك)' : ''}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="mb-3 flex items-start gap-2 rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-3 text-[11px] leading-relaxed text-cyan-200">
              <Icon name="info" size={14} className="mt-px shrink-0" />
              يوم فارغ لدى الفريق. وقت الانتقال {team.bufferMinutes} دقيقة لمكان مختلف، و
              {team.sameLocationBufferMinutes} دقيقة لتصوير آخر في نفس المكان.
            </p>
          )}

          {availability.loading ? (
            <Spinner variant="inline" label="جاري التحقق من المواعيد..." />
          ) : availability.data?.reason ? (
            <EmptyState icon="calendar-x" title={availability.data.reason} />
          ) : slots.length === 0 ? (
            <EmptyState
              icon="calendar-x"
              title="لا توجد مواعيد متاحة في هذا اليوم"
              message="جرّب يوماً آخر أو مدة أقصر."
            />
          ) : (
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-6">
              {slots.map((slot) => (
                <button
                  key={slot}
                  type="button"
                  onClick={() =>
                    setConfirming({
                      team,
                      date: dateKey,
                      startTime: slot,
                      hours: boundedHours,
                      location,
                    })
                  }
                  className="rounded-xl border border-white/10 bg-slate-900 p-2.5 text-xs font-bold text-slate-200 transition-colors hover:border-purple-500/60 hover:bg-slate-800"
                >
                  {formatTime12(slot)}
                </button>
              ))}
            </div>
          )}

          {slots.length > 0 && (
            <p className="mt-3 text-center text-sm text-slate-400">
              التكلفة: <span className="font-bold text-emerald-400">{formatCurrency(total)}</span>{' '}
              ({boundedHours} ساعة × {formatCurrency(team.hourlyRate)})
            </p>
          )}
        </Panel>
      )}

      {/* --- my shoots ------------------------------------------------------ */}
      <Panel>
        <SectionTitle icon="history">حجوزات التصوير الخاصة بي</SectionTitle>

        {bookingsState.loading && myBookings.length === 0 ? (
          <Spinner variant="inline" />
        ) : myBookings.length === 0 ? (
          <EmptyState icon="camera" title="لا توجد حجوزات تصوير بعد" />
        ) : (
          <div className="space-y-2">
            {myBookings.map((booking) => {
              const meta = STATUS_META[booking.status] || STATUS_META.pending;
              const live = ['pending', 'confirmed'].includes(booking.status);
              return (
                <div
                  key={booking.id}
                  className="flex flex-wrap items-center gap-3 rounded-xl border border-white/5 bg-white/5 p-3"
                >
                  <span className="rounded-lg bg-purple-500/10 p-2 text-purple-400">
                    <Icon name="camera" size={17} />
                  </span>

                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-bold text-white">
                      {booking.PhotographyTeam?.name || 'فريق تصوير'}
                    </p>
                    <p className="mt-0.5 flex flex-wrap items-center gap-x-3 text-[11px] text-slate-400">
                      <span>{isoToDisplayDate(booking.date) || booking.date}</span>
                      <span dir="ltr">{formatTime12(booking.startTime)}</span>
                      <span>{booking.durationHours} ساعة</span>
                      <span className="font-bold text-emerald-400">
                        {formatCurrency(booking.totalPrice)}
                      </span>
                    </p>
                    {booking.location && (
                      <p className="mt-0.5 truncate text-[11px] text-slate-500">
                        {booking.location}
                      </p>
                    )}
                  </div>

                  <Badge className={meta.className}>{meta.label}</Badge>

                  {live && (
                    <Button variant="ghost" icon="x" onClick={() => cancel(booking)}>
                      إلغاء
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Panel>

      {confirming && (
        <ConfirmShootModal
          draft={confirming}
          onClose={() => setConfirming(null)}
          onSubmit={submit}
        />
      )}

      {portfolioTeam && (
        <PortfolioModal team={portfolioTeam} onClose={() => setPortfolioTeam(null)} />
      )}
    </div>
  );
}

/* ========================================================================== */

function ConfirmShootModal({ draft, onClose, onSubmit }) {
  // The location came from the calendar step, because it decides which travel
  // gap applied to the slots on offer. Changing it here would invalidate the
  // slot that was just picked, so it is shown rather than edited.
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const total = Math.round((draft.team.hourlyRate || 0) * draft.hours);

  const confirm = async () => {
    setBusy(true);
    try {
      await onSubmit({
        teamId: draft.team.id,
        date: draft.date,
        startTime: draft.startTime,
        durationHours: draft.hours,
        location: (draft.location || '').trim(),
        notes: notes.trim(),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="تأكيد حجز التصوير"
      icon="camera"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            رجوع
          </Button>
          <Button icon="check" loading={busy} onClick={confirm}>
            تأكيد الحجز
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <dl className="grid grid-cols-2 gap-3 rounded-xl border border-white/5 bg-white/5 p-3 text-sm">
          <Detail label="الفريق" value={draft.team.name} />
          <Detail label="اليوم" value={isoToDisplayDate(draft.date) || draft.date} />
          <Detail label="البداية" value={formatTime12(draft.startTime)} />
          <Detail label="المدة" value={`${draft.hours} ساعة`} />
          <Detail label="سعر الساعة" value={formatCurrency(draft.team.hourlyRate)} />
          <Detail label="الإجمالي" value={formatCurrency(total)} strong />
          {draft.location && (
            <div className="col-span-2">
              <dt className="text-[11px] text-slate-400">المكان</dt>
              <dd className="font-bold text-white">{draft.location}</dd>
            </div>
          )}
        </dl>

        <Field label="ملاحظات للفريق" hint="اختياري">
          <Textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="مثال: تصوير ريلز قصيرة للعيادة وغرفة الكشف."
          />
        </Field>

        <p className="text-[11px] leading-relaxed text-slate-500">
          وقت الانتقال المحجوز لهذا التصوير محسوب على أساس المكان المذكور أعلاه. الحجز يبدأ
          «بانتظار التأكيد» حتى يؤكده الفريق.
        </p>
      </div>
    </Modal>
  );
}

function Detail({ label, value, strong = false }) {
  return (
    <div>
      <dt className="text-[11px] text-slate-400">{label}</dt>
      <dd className={strong ? 'font-bold text-emerald-400' : 'font-bold text-white'}>{value}</dd>
    </div>
  );
}

/* ========================================================================== */

/** Sample work, so a doctor chooses a crew by what they produce. */
function PortfolioModal({ team, onClose }) {
  const items = team.portfolio || [];

  return (
    <Modal open onClose={onClose} title={`أعمال ${team.name}`} icon="image" size="lg">
      {team.bio && <p className="mb-4 text-sm leading-relaxed text-slate-300">{team.bio}</p>}

      {items.length === 0 ? (
        <EmptyState icon="image" title="لا توجد أعمال معروضة بعد" />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {items.map((item, index) => (
            <figure
              key={`${item.name}-${index}`}
              className="overflow-hidden rounded-xl border border-white/10 bg-slate-900"
            >
              {String(item.data || '').startsWith('data:video/') ? (
                <video src={item.data} controls className="h-48 w-full bg-black object-contain" />
              ) : (
                <img
                  src={item.data}
                  alt={item.caption || item.name || ''}
                  className="h-48 w-full object-cover"
                />
              )}
              {(item.caption || item.name) && (
                <figcaption className="truncate px-3 py-2 text-[11px] text-slate-400">
                  {item.caption || item.name}
                </figcaption>
              )}
            </figure>
          ))}
        </div>
      )}
    </Modal>
  );
}

export default PhotographyBooking;
